import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { callRoute, type RouteCall } from "./client.ts";
import { invalidateAfter } from "./invalidation.ts";
import { routeKey, type MutationRouteName, type QueryRouteName } from "./query-keys.ts";

/*
 * The two building blocks under the composables per resource (07 §5, ADR-076): a read of a
 * route keyed by the route catalogue, and a change of a route that invalidates what it touches.
 * Pages use the named composables in src/composables/, not these directly.
 */

export type RouteQueryOptions = {
  /** false keeps the query idle, e.g. while a parameter is missing */
  enabled?: MaybeRefOrGetter<boolean>;
  /** keeps the last answer on screen while a changed query loads, e.g. the next page of a list */
  keepPrevious?: boolean;
};

/** Reads route `name`; a reactive `call` refetches when its parameters or query change. */
export function useRouteQuery<N extends QueryRouteName>(
  name: N,
  call: MaybeRefOrGetter<Omit<RouteCall<N>, "body" | "signal">> = {},
  options: RouteQueryOptions = {},
) {
  return useQuery({
    queryKey: computed(() => {
      const current = toValue(call);
      return routeKey(name, current.params, (current.query ?? {}) as Record<string, unknown>);
    }),
    queryFn: ({ signal }) => callRoute(name, { ...toValue(call), signal } as RouteCall<N>),
    enabled: computed(() => toValue(options.enabled ?? true)),
    ...(options.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

export type RouteMutationOptions = {
  /**
   * How long the change, its answer included, stays in the mutation cache once nothing observes
   * it (a reset, an unmount); 0 drops an answer that must not linger, such as a key in plaintext
   * (R-13). Vue Query's default otherwise.
   */
  gcTime?: number;
};

/**
 * Changes through route `name`; on success the reads listed in `invalidates` refetch. The key is
 * the route's name, so the query client can name the route of a failure (PF4).
 */
export function useRouteMutation<N extends MutationRouteName>(name: N, options: RouteMutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [name],
    mutationFn: (call: Omit<RouteCall<N>, "signal">) => callRoute(name, call),
    onSuccess: () => invalidateAfter(queryClient, name),
    ...(options.gcTime === undefined ? {} : { gcTime: options.gcTime }),
  });
}
